'use client';

import {
  type ChangeEvent,
  type ChangeEventHandler,
  type HTMLAttributes,
  type ReactNode,
  type Ref,
  useCallback,
  useEffect,
  useEffectEvent,
  useId,
  useMemo,
  useRef,
  useState,
} from 'react';
import { clsx } from 'clsx';
import { useCombobox } from 'downshift';
import { matchSorter } from 'match-sorter';
import { sortByKey } from '@interxfi/utilities/sort';
import { FormHint, type FormHintVariant } from '../../../components/form/hint';
import ClearIcon from '../../../icons/react/icon-clear';
import DisclosureDownIcon from '../../../icons/react/icon-disclosure-down';
import DisclosureUpIcon from '../../../icons/react/icon-disclosure-up';
import InfoIcon from '../../../icons/react/icon-info';
import PlusIcon from '../../../icons/react/icon-plus';
import { useControlledValue } from '../../../util/controlled';
import { HorizontalStack } from '../../alignment/horizontal-stack';
import { VerticalStack } from '../../alignment/vertical-stack';
import { PlainIconButton } from '../../button/plain-icon/plain-icon-button';
import { Tooltip } from '../../tooltip/tooltip';
import { ErrorMessage } from '../error-message/error-message';
import { InputHint } from '../input-hint/input-hint';
import { Label } from '../label/label';
import {
  InputDropdown,
  type InputDropdownOption,
  type InputDropdownPagination,
  inputDropdownOptionToString,
  useFloatingDropdown,
} from '../shared/dropdown/input-dropdown';
import { useFocusWithin } from '../shared/use-focus-within';
import inputStyles from '../input.module.scss';
import styles from './combobox-input.module.scss';

function buildAddOption(
  label: string | undefined,
  icon: ReactNode | undefined,
  value: string | undefined,
  search: string | undefined,
): InputDropdownOption | undefined {
  if (label) {
    return {
      prefix: icon ?? <PlusIcon aria-hidden width={16} />,
      label,
      value: value ?? search ?? '',
    };
  }

  return undefined;
}

function buildOptions({
  options,
  search,
  hasControlledFilters,
  addOptionLabel,
  addOptionIcon,
  addOptionValue,
  optionsToShow,
}: {
  options: InputDropdownOption[];
  search: string;
  hasControlledFilters?: boolean;
  addOptionLabel?: string;
  addOptionIcon?: ReactNode;
  addOptionValue?: string;
  optionsToShow?: number;
}) {
  let result = [...options];

  // Filtering (only if uncontrolled)
  if (!hasControlledFilters && search) {
    result = matchSorter(result, search, {
      keys: ['label', 'aliases'],
    });
  }

  result = sortByKey(result, (option) => option?.group || '');

  if (optionsToShow) {
    result = result.slice(0, optionsToShow);
  }

  const addOption = buildAddOption(addOptionLabel, addOptionIcon, addOptionValue, search);

  if (addOption) {
    result.unshift(addOption);
  }

  return result;
}

export interface ComboboxInputProps extends Omit<HTMLAttributes<HTMLSelectElement>, 'prefix'> {
  addOptionIcon?: ReactNode;
  addOptionLabel?: string;
  addOptionValue?: string;
  allowClear?: boolean;
  disabled?: boolean;
  error?: string;
  fillParent?: boolean;
  hasControlledFilters?: boolean;
  hint?: ReactNode;
  hintVariant?: FormHintVariant;
  info?: ReactNode;
  inputValue?: string;
  isLoading?: boolean;
  label: string;
  name: string;
  noMatchingOptionsMessage?: ReactNode;
  onAddNewOption?: (value: string) => void;
  options: InputDropdownOption[];
  optionsToShow?: number;
  onChange?: ChangeEventHandler<HTMLSelectElement>;
  onInputValueChange?: (value: string) => void;
  pagination?: InputDropdownPagination;
  placeholder?: string;
  ref?: Ref<HTMLInputElement>;
  required?: boolean;
  suffix?: ReactNode;
  touched?: boolean;
  value: string;
}

export function ComboboxInput({
  addOptionIcon,
  addOptionLabel,
  addOptionValue,
  allowClear = true,
  className,
  disabled,
  error,
  fillParent,
  hasControlledFilters,
  hint,
  hintVariant,
  info,
  id: providedId,
  inputValue: controlledInputValue,
  isLoading,
  label,
  name,
  noMatchingOptionsMessage = 'No results found',
  onAddNewOption,
  onChange,
  onInputValueChange,
  options,
  optionsToShow,
  pagination,
  placeholder,
  ref,
  required,
  suffix,
  touched,
  value,
  ...rest
}: ComboboxInputProps) {
  const hasInitializedRef = useRef(false);
  const [hasEnteredSearchSinceOpen, setHasEnteredSearchSinceOpen] = useState(false);
  const initialSelectedItem = options.find((option) => option.value === value);
  const initialSearchValue = initialSelectedItem?.label ?? '';
  const [search, setSearch] = useControlledValue<string>(controlledInputValue || initialSearchValue, onInputValueChange, initialSearchValue);
  const listRef = useRef<HTMLDivElement>(null);
  const [hasFocus, handleFocusWithin, handleBlurWithin] = useFocusWithin();
  const generatedId = useId();
  const id = providedId || generatedId;
  const labelId = `${id}-label`;
  const menuId = `${id}-menu`;
  const inputId = `${id}-input`;
  const errorId = `${id}-error`;
  const hintId = `${id}-hint`;
  const showError = Boolean(touched && error && value);
  const computedOptions = useMemo(
    () =>
      buildOptions({
        options,
        search: hasEnteredSearchSinceOpen ? search : '',
        hasControlledFilters,
        addOptionLabel,
        addOptionIcon,
        addOptionValue,
        optionsToShow,
      }),
    [options, search, hasControlledFilters, hasEnteredSearchSinceOpen, addOptionLabel, addOptionIcon, addOptionValue, optionsToShow],
  );

  const hasSearch = Boolean(search);

  // Initialize search with the label of the selected value on first render (if not controlled and if there is a selected value)
  useEffect(() => {
    if (hasSearch) {
      hasInitializedRef.current = true;
    } else {
      if (initialSearchValue && !hasInitializedRef.current) {
        setSearch(initialSearchValue);
      }
    }
  }, [hasSearch, initialSearchValue, setSearch]);

  const {
    getItemProps,
    getMenuProps,
    getLabelProps,
    getInputProps,
    highlightedIndex,
    isOpen,
    closeMenu,
    selectedItem,
    selectItem,
    openMenu,
    inputValue,
  } = useCombobox({
    id,
    menuId,
    labelId,
    inputId,
    inputValue: search,
    onIsOpenChange({ isOpen: newIsOpen, inputValue: newInputValue, selectedItem: newSelectedItem }) {
      if (!newIsOpen) {
        setHasEnteredSearchSinceOpen(false);
      }

      if (newIsOpen && !newInputValue && newSelectedItem && computedOptions.some((option) => option.value === newSelectedItem.value)) {
        setSearch(newSelectedItem.label);
      }
    },
    onSelectedItemChange: ({ selectedItem: newSelectedItem }) => {
      onChange?.({
        target: {
          name,
          value: newSelectedItem?.value || '',
        },
      } as ChangeEvent<HTMLSelectElement>);
    },
    onInputValueChange: ({ inputValue: newInputValue, isOpen: newIsOpen }) => {
      if (selectedItem?.label === inputValue && newInputValue !== selectedItem.label) {
        handleClear();
      } else {
        if (newIsOpen) {
          setHasEnteredSearchSinceOpen(true);
          setSearch(newInputValue);
        }
      }
    },
    items: computedOptions,
    itemToString: inputDropdownOptionToString,
  });

  const handleClear = useCallback(() => {
    onChange?.({
      target: {
        name,
        value: '',
      },
    } as ChangeEvent<HTMLSelectElement>);

    setSearch('');
    selectItem(null);
  }, [onChange, setSearch, name, selectItem]);

  const hasValue = Boolean(value);

  const resetIfSelectedValueIsLingering = useEffectEvent(() => {
    selectItem(null);
    setSearch('');
  });

  useEffect(() => {
    if (!hasValue) {
      resetIfSelectedValueIsLingering();
    }
  }, [hasValue]);

  const { refs, floatingStyles } = useFloatingDropdown(computedOptions.length, isOpen, true, 'bottom', { floating: listRef.current });

  const listboxProps = useMemo(
    () => ({
      ...getMenuProps({ ref: listRef }),
      style: floatingStyles,
      hidden: !isOpen,
    }),
    [floatingStyles, getMenuProps, isOpen],
  );
  const triggerHasValue = Boolean(selectedItem || placeholder || inputValue || hasFocus);

  return (
    <VerticalStack
      className={clsx(
        inputStyles.input,
        styles.comboboxInput,
        triggerHasValue && styles.hasValue,
        disabled && inputStyles.isDisabled,
        disabled && styles.disabled,
        fillParent && styles.flexGrow,
        className,
      )}
      onBlurCapture={handleBlurWithin}
      onFocusCapture={handleFocusWithin}
    >
      <VerticalStack>
        <HorizontalStack className={clsx(inputStyles.activator)}>
          <Label {...getLabelProps({ label, className: styles.label })} hasError={showError} isFloating={triggerHasValue} required={required} />

          <HorizontalStack
            align="space-between"
            blockAlign="center"
            className={clsx(styles.triggerContainer, styles.inputElement, !showError && !hint && styles.lastInputElement)}
            ref={refs.setReference}
            wrap="nowrap"
          >
            <HorizontalStack align="start" blockAlign="center" className={styles.item} gap={2} wrap="nowrap">
              {/* {selectedItem?.prefix && <span className={styles.prefix}>{selectedItem.prefix}</span>} */}

              <input
                aria-describedby={hint ? hintId : undefined}
                aria-errormessage={showError ? errorId : undefined}
                aria-invalid={showError}
                aria-required={required}
                className={clsx(styles.trigger, selectedItem?.className)}
                placeholder={placeholder}
                {...getInputProps({ disabled, ref, required, ...(rest as unknown as HTMLAttributes<HTMLInputElement>) })}
              />
            </HorizontalStack>

            <HorizontalStack blockAlign="center" className={styles.iconContainer} gap={4} wrap="nowrap">
              {suffix}

              {allowClear && !disabled && selectedItem ? (
                <button aria-controls={id} aria-label="Clear selection" className={styles.clearButton} onClick={handleClear} type="button">
                  <ClearIcon aria-hidden />
                </button>
              ) : null}

              {info ? (
                <Tooltip
                  activator={
                    <PlainIconButton>
                      <InfoIcon aria-hidden height={16} />
                    </PlainIconButton>
                  }
                  maxWidth={136}
                  side="top"
                  size="small"
                >
                  {info}
                </Tooltip>
              ) : null}

              {isOpen ? (
                <DisclosureUpIcon aria-hidden className={styles.toggleIcon} onClick={!disabled ? closeMenu : undefined} />
              ) : (
                <DisclosureDownIcon aria-hidden className={styles.toggleIcon} onClick={!disabled ? openMenu : undefined} />
              )}
            </HorizontalStack>
          </HorizontalStack>
        </HorizontalStack>

        {hint ? (
          hintVariant ? (
            <FormHint className={clsx(styles.inputElement, !showError && styles.lastInputElement)} size="small" variant={hintVariant}>
              {hint}
            </FormHint>
          ) : (
            <InputHint className={clsx(styles.inputElement, !showError && styles.lastInputElement)} id={hintId}>
              {hint}
            </InputHint>
          )
        ) : null}

        {showError ? (
          <ErrorMessage className={clsx(styles.inputElement, styles.lastInputElement)} id={errorId}>
            {error}
          </ErrorMessage>
        ) : null}
      </VerticalStack>

      <InputDropdown
        getItemProps={getItemProps as any}
        highlightedIndex={highlightedIndex}
        inputId={id}
        isLoading={isLoading}
        noOptionsMessage={inputValue ? noMatchingOptionsMessage : 'No Results'}
        options={computedOptions}
        pagination={pagination}
        selectedItemValue={selectedItem?.value}
        {...listboxProps}
      />
    </VerticalStack>
  );
}
